import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronUp, ChevronDown, Search } from "lucide-react";

export default function DataTable({ columns, rows, keyField = "id", searchable = true, onRowClick, testid,
                                   selectable = false, selected, onSelectionChange,
                                   loading = false, skeletonRows = 6 }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const filtered = useMemo(() => {
    let out = rows || [];
    if (q) {
      const lq = q.toLowerCase();
      out = out.filter(r =>
        columns.some(c => {
          const v = c.accessor ? c.accessor(r) : r[c.key];
          return String(v ?? "").toLowerCase().includes(lq);
        })
      );
    }
    if (sortKey) {
      out = [...out].sort((a, b) => {
        const col = columns.find(c => c.key === sortKey);
        const av = col?.accessor ? col.accessor(a) : a[sortKey];
        const bv = col?.accessor ? col.accessor(b) : b[sortKey];
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [rows, q, sortKey, sortDir, columns]);

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const allVisibleSelected = selectable && filtered.length > 0 &&
    filtered.every(r => selected?.has(r[keyField]));
  const toggleAll = () => {
    if (!onSelectionChange) return;
    const next = new Set(selected || []);
    if (allVisibleSelected) {
      filtered.forEach(r => next.delete(r[keyField]));
    } else {
      filtered.forEach(r => next.add(r[keyField]));
    }
    onSelectionChange(next);
  };
  const toggleRow = (id, e) => {
    e.stopPropagation();
    if (!onSelectionChange) return;
    const next = new Set(selected || []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(next);
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden" data-testid={testid}>
      {searchable && (
        <div className="p-4 border-b border-neutral-200 flex items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Zoeken…"
              className="pl-9 h-9"
              data-testid={`${testid}-search`}
            />
          </div>
          <span className="text-xs text-neutral-500 ml-auto">{filtered.length} resultaten</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full data-table text-sm">
          <thead>
            <tr>
              {selectable && (
                <th className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleAll}
                    data-testid={`${testid}-select-all`}
                  />
                </th>
              )}
              {columns.map(c => (
                <th
                  key={c.key}
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                  className={`text-left px-4 py-3 ${c.sortable !== false ? "cursor-pointer select-none" : ""} ${c.className || ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sortKey === c.key && (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading && (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`} data-testid={`${testid}-skeleton-row-${i}`}>
                  {selectable && (
                    <td className="px-3 py-3"><Skeleton className="h-4 w-4" /></td>
                  )}
                  {columns.map((c, ci) => (
                    <td key={c.key} className={`px-4 py-3 ${c.className || ""}`}>
                      <Skeleton className={ci === 0 ? "h-4 w-32" : "h-4 w-20"} />
                    </td>
                  ))}
                </tr>
              ))
            )}
            {!loading && filtered.map(r => (
              <tr
                key={r[keyField]}
                onClick={() => onRowClick && onRowClick(r)}
                className={onRowClick ? "cursor-pointer" : ""}
                data-testid={`${testid}-row-${r[keyField]}`}
              >
                {selectable && (
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected?.has(r[keyField]) || false}
                      onCheckedChange={() => toggleRow(r[keyField], { stopPropagation: () => {} })}
                      data-testid={`${testid}-select-${r[keyField]}`}
                    />
                  </td>
                )}
                {columns.map(c => (
                  <td key={c.key} className={`px-4 py-3 text-neutral-800 ${c.className || ""}`}>
                    {c.render ? c.render(r) : (c.accessor ? c.accessor(r) : r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-8 text-center text-neutral-500 text-sm">
                  Geen resultaten
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
